import React, { useMemo } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const abbreviate = (_label, index) => `C${index + 1}`;

const RadarChart = ({ jobData }) => {
  const chartSummary = useMemo(() => {
    const totals = new Map();
    const counts = new Map();

    jobData.forEach((job) => {
      const scores = job?.score_user?.details || {};
      Object.entries(scores).forEach(([label, value]) => {
        totals.set(label, (totals.get(label) || 0) + (Number(value) || 0));
        counts.set(label, (counts.get(label) || 0) + 1);
      });
    });

    const rawLabels = Array.from(totals.keys());
    const shortLabels = rawLabels.map((_, i) => abbreviate(_, i));
    const values = rawLabels.map((label) => {
      const total = totals.get(label) || 0;
      const count = counts.get(label) || 1;
      return Math.round((total / count) * 10) / 10;
    });

    return { rawLabels, shortLabels, values };
  }, [jobData]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          ticks: {
            beginAtZero: true,
            max: 10,
            display: true,
            stepSize: 2,
            backdropColor: "transparent",
            color: "rgba(255, 255, 255, 0.55)",
            font: { size: 9 },
          },
          angleLines: {
            color: "rgba(255, 255, 255, 0.12)",
          },
          grid: {
            color: "rgba(255, 255, 255, 0.12)",
          },
          pointLabels: {
            color: "rgba(255, 255, 255, 0.92)",
            font: { size: 13, weight: "700" },
            padding: 8,
          },
          suggestedMin: 0,
          suggestedMax: 10,
        },
      },
      layout: {
        padding: { top: 8, bottom: 8, left: 8, right: 8 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1F232C",
          borderColor: "rgba(59, 162, 166, 0.35)",
          borderWidth: 1,
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          displayColors: false,
          callbacks: {
            title: (context) => {
              const index = context[0]?.dataIndex;
              return chartSummary.rawLabels[index] || "";
            },
            label: (context) => `Avg: ${context.raw}/10`,
          },
        },
      },
    }),
    [chartSummary.rawLabels]
  );

  const data = useMemo(
    () => ({
      labels: chartSummary.shortLabels,
      datasets: [
        {
          label: "Average Scores",
          data: chartSummary.values,
          backgroundColor: "rgba(59, 162, 166, 0.15)",
          borderColor: "rgba(255, 255, 255, 0.88)",
          borderWidth: 2,
          pointBackgroundColor: "#FFFFFF",
          pointBorderColor: "#FFFFFF",
          pointHoverBackgroundColor: "#3BA2A6",
          pointHoverBorderColor: "#FFFFFF",
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: true,
        },
      ],
    }),
    [chartSummary]
  );

  if (!chartSummary.rawLabels.length) {
    return null;
  }

  return (
    <div className="radar-chart-wrapper">
      <div className="radar-chart-container">
        <Radar data={data} options={options} />
      </div>
      <ul className="radar-legend">
        {chartSummary.rawLabels.map((label, i) => (
          <li key={label} className="radar-legend-item">
            <span className="radar-legend-key">C{i + 1}</span>
            <span className="radar-legend-label">{label}</span>
            <span className="radar-legend-value">{chartSummary.values[i]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RadarChart;